/**
 * CreateDuelWizard — 4-step duel creation flow.
 *
 * Steps extracted into components/duel-wizard/.
 * This file retains all state + business logic.
 */

import { useState, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View, Text, StyleSheet, Modal, TouchableOpacity, Alert, Platform,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { X } from 'lucide-react-native';
import { findMatchedOpponents, type MatchResult } from '@/lib/matchmaking';
import { hapticsUtil } from '@/lib/haptics';
import { V1_LIFT_LABELS } from '@/lib/lifts/canonical';
import {
  TemplatesStep, OpponentStep, TypeStep, DetailsStep, ReviewStep,
  type Step, type DuelType, type DuelOpponent, type Exercise,
} from '@/components/duel-wizard';
import type { DuelTemplate } from '@/components/duel-wizard/TemplatesStep';

import { trackEvent } from '@/lib/analytics';
import { captureError } from '@/lib/errorReporting';
import { isRateLimited } from '@/lib/rateLimiter';
import { createDuelInvite } from '@/lib/duelInvites';
import { notifyDuelChallenge } from '@/lib/notificationTriggers';
import { useUserGyms, type UserGym } from '@/hooks/useUserGyms';
import { useSubscription } from '@/hooks/useSubscription';
import GymSwitcherPills from '@/components/GymSwitcherPills';
import * as Clipboard from 'expo-clipboard';

const FREE_ACTIVE_DUEL_LIMIT = 3;

const log = createLogger('DUEL_WIZARD');

interface CreateDuelWizardProps {
  visible: boolean;
  onClose: () => void;
  onDuelCreated: () => void;
  preselectedOpponentId?: string;
}

export default function CreateDuelWizard({
  visible,
  onClose,
  onDuelCreated,
  preselectedOpponentId,
}: CreateDuelWizardProps) {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const { isProPlus } = useSubscription();
  const c = theme.colors;
  const { gyms: userGyms, activeGym: activeUserGym, setActiveGym: setActiveUserGym } = useUserGyms();

  const [currentStep, setCurrentStep] = useState<Step>('templates');
  const [selectedOpponent, setSelectedOpponent] = useState<DuelOpponent | null>(null);
  const [duelType, setDuelType] = useState<DuelType | null>(null);
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [duration, setDuration] = useState(24);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [, setUsers] = useState<DuelOpponent[]>([]);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [, setLoadingUsers] = useState(false);
  const [loadingExercises, setLoadingExercises] = useState(false);
  const [matchResult, setMatchResult] = useState<MatchResult | null>(null);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [showInviteShare, setShowInviteShare] = useState(false);
  const [templateUsed, setTemplateUsed] = useState(false);

  const loadMatchedOpponents = async () => {
    if (!profile?.id) return;
    setLoadingMatch(true);
    try {
      const score = (profile as any).lifting_score ?? 0;
      const result = await findMatchedOpponents(profile.id, score, profile.gym_id ?? null, (profile as any).gender ?? null);
      setMatchResult(result);
    } catch (error: unknown) {
      log.error('Matchmaking failed:', error);
    } finally {
      setLoadingMatch(false);
    }
  };

  useEffect(() => {
    if (visible) {
      loadMatchedOpponents();
      loadExercises();
      if (preselectedOpponentId) {
        const loadPreselectedOpponent = async () => {
          const { data } = await supabase
            .from('profiles')
            .select('id, handle, display_name, avatar_url, gym_id')
            .eq('id', preselectedOpponentId)
            .maybeSingle();
          if (data) {
            const row = data as DuelOpponent & { user_id?: string; handle?: string | null };
            setSelectedOpponent({
              id: row.id,
              handle: row.handle ?? null,
              display_name: row.display_name,
              avatar_url: row.avatar_url,
              gym_id: row.gym_id,
            });
            setCurrentStep('type');
          }
        };
        loadPreselectedOpponent();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const loadUsers = async () => {
    if (!profile?.id) return;
    setLoadingUsers(true);
    try {
      const gymId = profile.gym_id ?? null;

      if (gymId) {
        // Prefer gym-based opponents
        const { data, error } = await supabase.rpc('get_duel_opponents_for_gym', {
          p_gym_id: gymId,
          p_user_id: profile.id,
        });
        if (error) throw error;
        if (data && (data as DuelOpponent[]).length > 0) {
          setUsers((data as DuelOpponent[]) || []);
          return;
        }
      }

      // Fallback: show all users when no gym or no gym matches found
      const { data: allUsers, error: allError } = await supabase
        .from('profiles')
        .select('id, handle, display_name, avatar_url, gym_id')
        .neq('id', profile.id)
        .not('handle', 'is', null)
        .order('display_name')
        .limit(50);

      if (allError) throw allError;
      setUsers((allUsers as DuelOpponent[]) || []);
    } catch (error: unknown) {
      log.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadExercises = async () => {
    setLoadingExercises(true);
    try {
      const { data, error } = await supabase
        .from('exercises')
        .select('id, name, category')
        .order('name');
      if (error) throw error;
      setExercises(data || []);
    } catch (error: unknown) {
      log.error('Failed to load exercises:', error);
    } finally {
      setLoadingExercises(false);
    }
  };

  const filteredExercises = exercises.filter((ex) =>
    ex.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const FRESHNESS_COOLDOWN_MS = 10 * 60 * 1000;

  const checkFreshnessCooldown = (): boolean => {
    const updatedAt = (profile as any)?.duel_standard_updated_at;
    if (!updatedAt) return false;
    const elapsed = Date.now() - new Date(updatedAt).getTime();
    return elapsed < FRESHNESS_COOLDOWN_MS;
  };

  const handleCreateDuel = async () => {
    if (checkFreshnessCooldown()) {
      const msg = 'Your strength score was recently updated. A 10-minute cooldown prevents score manipulation before duel creation.';
      if (Platform.OS === 'web') {
        const proceed = confirm(msg + '\n\nContinue anyway?');
        if (proceed) await executeCreateDuel();
      } else {
        Alert.alert(
          'Activity Cooldown',
          msg,
          [
            { text: 'Wait', style: 'cancel' },
            { text: 'Continue Anyway', onPress: () => executeCreateDuel() },
          ],
        );
      }
      return;
    }
    await executeCreateDuel();
  };

  const executeCreateDuel = async () => {
    if (!profile?.id || !selectedOpponent || !duelType) return;
    if (isRateLimited('duel_create', 5, 3_600_000)) {
      Alert.alert('Slow down', 'Too many duels created. Please wait a moment.');
      return;
    }

    // Per-opponent cooldown: max 1 active duel per opponent pair at a time
    const { count: pairCount } = await supabase
      .from('duels')
      .select('id', { count: 'exact', head: true })
      .in('status', ['active', 'pending'])
      .or(`and(challenger_id.eq.${profile.id},challenged_id.eq.${selectedOpponent.id}),and(challenger_id.eq.${selectedOpponent.id},challenged_id.eq.${profile.id})`);
    if ((pairCount ?? 0) > 0) {
      Alert.alert('Duel Already Active', 'You already have an active or pending duel with this opponent. Finish it before starting another.');
      return;
    }

    // Free users: limit to 3 active duels
    if (!isProPlus) {
      const { count, error: countErr } = await supabase
        .from('duels')
        .select('id', { count: 'exact', head: true })
        .eq('challenger_id', profile.id)
        .in('status', ['active', 'pending']);
      if (!countErr && (count ?? 0) >= FREE_ACTIVE_DUEL_LIMIT) {
        Alert.alert(
          'Duel Limit Reached',
          `Free accounts can have up to ${FREE_ACTIVE_DUEL_LIMIT} active duels. Upgrade to Pro for unlimited duels.`,
          [
            { text: 'OK', style: 'cancel' },
            { text: 'View Plans', onPress: () => { onClose(); import('expo-router').then(({ router }) => router.push('/paywall' as any)); } },
          ],
        );
        return;
      }
    }

    setLoading(true);
    try {
      const needsExercise = duelType === 'pr' || duelType === '1rm' || duelType === 'strength';
      const exerciseId = needsExercise ? selectedExercise?.id ?? null : null;
      const exerciseName = needsExercise
        ? selectedExercise?.name ?? null
        : selectedCategory ?? 'Total Volume';
      const category = selectedCategory ?? null;

      // gym_id — use selected gym from multi-gym switcher, fallback to profile gym
      const gymId = activeUserGym?.id ?? (profile as any).gym_id ?? null;

      const { data, error } = await supabase
        .from('duels')
        .insert({
          gym_id: gymId,
          challenger_id: profile.id,
          challenged_id: selectedOpponent.id,
          opponent_id: selectedOpponent.id,
          duel_type: duelType,
          exercise_id: exerciseId,
          exercise_name: exerciseName,
          lift_type: category || exerciseName,
          timeframe_hours: duration,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;

      trackEvent('duel_created', { duelType, duration, opponentId: selectedOpponent.id });
      await hapticsUtil.duelCreated();

      // Fire-and-forget: notify challenged user
      const challengerName = profile.handle || profile.display_name || 'Someone';
      notifyDuelChallenge(selectedOpponent.id, challengerName, duelType, data.id).catch(() => {});

      // Generate invite link for sharing
      try {
        const { url } = await createDuelInvite(data.id, profile.id);
        setInviteUrl(url);
        setShowInviteShare(true);
      } catch {
        // Non-critical — still proceed
      }

      onDuelCreated();

      if (!inviteUrl) {
        if (Platform.OS === 'web') {
          alert('Challenge Sent');
        } else {
          Alert.alert('Sent', 'Challenge Sent');
        }
        resetWizard();
      }
    } catch (error: unknown) {
      log.error('Failed to create duel:', error);

      if (Platform.OS === 'web') {
        alert("Couldn't create the duel. Please try again.");
      } else {
        Alert.alert('Oops', "Couldn't create the duel. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setCurrentStep('templates');
    setTemplateUsed(false);
    setSelectedOpponent(null);
    setDuelType(null);
    setSelectedExercise(null);
    setSelectedCategory(null);
    setDuration(24);
    setSearchQuery('');
    setMatchResult(null);
    setInviteUrl(null);
    setShowInviteShare(false);
  };

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  // Android hardware back — step backward through wizard instead of dismissing
  const handleAndroidBack = () => {
    switch (currentStep) {
      case 'type': setCurrentStep('templates'); break;
      case 'opponent': setCurrentStep('type'); break;
      case 'details': setCurrentStep('opponent'); break;
      case 'review': setCurrentStep(duelType === 'consistency' ? 'type' : 'details'); break;
      default: handleClose(); break;
    }
  };

  const handleSelectTemplate = (template: DuelTemplate) => {
    if (template.prefill === null) {
      // Custom — go straight to opponent step (full wizard)
      setTemplateUsed(false);
      setCurrentStep('opponent');
      return;
    }
    const { duelType: dt, exercise, duration: dur } = template.prefill;
    setDuelType(dt);
    setDuration(dur);
    if (exercise) {
      setSelectedExercise(exercise);
    }
    setTemplateUsed(true);
    // Skip to opponent selection with everything pre-filled
    setCurrentStep('opponent');
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'templates':
        return (
          <TemplatesStep
            colors={c}
            onSelectTemplate={handleSelectTemplate}
          />
        );
      case 'opponent':
        return (
          <View style={{ flex: 1 }}>
            {userGyms.length >= 2 && (
              <View style={{ paddingHorizontal: 16, paddingTop: 8 }}>
                <Text style={{ color: c.textSecondary, fontSize: 13, fontWeight: '600', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Challenge someone at:
                </Text>
                <GymSwitcherPills
                  gyms={userGyms}
                  activeGymId={activeUserGym?.id ?? null}
                  onSelect={setActiveUserGym}
                  colors={c}
                />
              </View>
            )}
            <OpponentStep
              colors={c}
              loadingMatch={loadingMatch}
              matchResult={matchResult}
              onSelectOpponent={(opp) => { setSelectedOpponent(opp); setCurrentStep(duelType ? 'review' : 'type'); }}
              onRefresh={loadMatchedOpponents}
            />
          </View>
        );
      case 'type':
        return (
          <TypeStep
            colors={c}
            duelType={duelType}
            onSelectType={(t) => {
              setDuelType(t);
              // Consistency duels don't need exercise/category selection
              if (t === 'consistency') {
                setCurrentStep('review');
              } else {
                setCurrentStep('details');
              }
            }}
            onBack={() => setCurrentStep('opponent')}
          />
        );
      case 'details':
        return (
          <DetailsStep
            colors={c}
            duelType={duelType}
            exercises={exercises}
            filteredExercises={filteredExercises}
            selectedExercise={selectedExercise}
            selectedCategory={selectedCategory}
            searchQuery={searchQuery}
            loadingExercises={loadingExercises}
            onSearchChange={setSearchQuery}
            onSelectExercise={(ex) => { setSelectedExercise(ex); setCurrentStep('review'); }}
            onSelectCategory={(id) => { setSelectedCategory(id); setCurrentStep('review'); }}
            onBack={() => setCurrentStep('type')}
          />
        );
      case 'review':
        return (
          <ReviewStep
            colors={c}
            selectedOpponent={selectedOpponent}
            duelType={duelType}
            selectedExercise={selectedExercise}
            selectedCategory={selectedCategory}
            duration={duration}
            loading={loading}
            onDurationChange={setDuration}
            onSubmit={handleCreateDuel}
            onBack={() => setCurrentStep(templateUsed ? 'opponent' : (duelType === 'consistency' ? 'type' : 'details'))}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={handleAndroidBack}>
      <View style={[styles.container, { backgroundColor: c.background }]}>
        <View style={[styles.header, { borderBottomColor: c.border }]}>
          <TouchableOpacity onPress={handleClose}>
            <X size={24} color={c.text} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: c.text }]}>Create Duel</Text>
          <View style={{ width: 24 }} />
        </View>
        {showInviteShare && inviteUrl ? (
          <View style={styles.inviteShareContainer}>
            <Text style={[styles.inviteTitle, { color: c.text }]}>Challenge Sent! ⚔️</Text>
            <Text style={[styles.inviteSubtitle, { color: c.textSecondary ?? c.text }]}>
              Share this link to invite your opponent:
            </Text>
            <Text style={[styles.inviteLink, { color: c.accent }]} selectable>
              {inviteUrl}
            </Text>
            <TouchableOpacity
              style={[styles.inviteButton, { backgroundColor: c.accent }]}
              onPress={async () => {
                try {
                  await Clipboard.setStringAsync(inviteUrl);
                  if (Platform.OS === 'web') {
                    alert('Link copied!');
                  } else {
                    Alert.alert('Copied', 'Invite link copied to clipboard');
                  }
                } catch {
                  // fallback
                }
              }}
            >
              <Text style={styles.inviteButtonText}>Copy Link</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.inviteDismiss}
              onPress={() => { resetWizard(); handleClose(); }}
            >
              <Text style={{ color: c.textSecondary ?? c.text, fontFamily: 'Inter_500Medium' }}>Done</Text>
            </TouchableOpacity>
          </View>
        ) : renderCurrentStep()}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1,
    paddingTop: Platform.OS === 'ios' ? 50 : 12,
  },
  headerTitle: { fontSize: 18, fontWeight: '700' },
  inviteShareContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
  inviteTitle: { fontSize: 24, fontFamily: 'Inter_700Bold', marginBottom: 12 },
  inviteSubtitle: { fontSize: 16, fontFamily: 'Inter_400Regular', marginBottom: 16, textAlign: 'center' },
  inviteLink: { fontSize: 14, fontFamily: 'Inter_500Medium', marginBottom: 24, textAlign: 'center' },
  inviteButton: { paddingVertical: 14, paddingHorizontal: 40, borderRadius: 0, marginBottom: 16 },
  inviteButtonText: { color: '#fff', fontSize: 16, fontFamily: 'Inter_600SemiBold' },
  inviteDismiss: { paddingVertical: 10 },
});
